function setStyleToFlex(id: string) {
  let element = document.getElementById(id);
  if (element) {
    element.style.display = "flex";
  }
}

function setStyleToInlineBlock(id: string) {
  let element = document.getElementById(id);
  if (element) {
    element.style.display = "inline-block";
  }
}

function setStyleToNone(id: string) {
  let element = document.getElementById(id);
  if (element) {
    element.style.display = "none";
  }
}

function setStyleToBlock(id: string) {
  let element = document.getElementById(id);
  if (element) {
    element.style.display = "block";
  }
}

function setOpacity(id: string, opacity: string) {
  let element = document.getElementById(id);
  if (element) {
    element.style.opacity = opacity;
  }
}

function setDisabled(id: string) {
  let element = document.getElementById(id) as HTMLInputElement;
  if (element) {
    element.disabled = true;
  }
}

function setMessage(id: string, message: string) {
  let element = document.getElementById(id);
  if (element) {
    element.innerHTML = message;
  }
}

function addHideOnEscapeKey(ids: string[]) {
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      for (let id of ids) {
        setStyleToNone(id);
      }
    }
  });
}

/** JSDoc */
class ImageSwapper {
  originalSrc: string;
  newSrc: string;
  originalElement: HTMLImageElement;
  newElement: HTMLImageElement;

  constructor(originalElementId: string, newElementId: string) {
    this.originalElement = document.getElementById(
      originalElementId
    ) as HTMLImageElement;
    this.newElement = document.getElementById(newElementId) as HTMLImageElement;
    this.originalSrc = this.getSrc(this.originalElement);
    this.newSrc = this.getSrc(this.newElement);
  }

  swap() {
    if (this.originalElement.src == this.originalSrc) {
      this.originalElement.src = this.newSrc;
    } else {
      this.originalElement.src = this.originalSrc;
    }
  }

  private getSrc(imageElement: HTMLImageElement): string {
    if (imageElement) {
      return imageElement.src;
    }
    return "";
  }
}

function throwExpression(errorMessage: string): never {
  throw new Error(errorMessage);
}

function throwMissingElement(elementId: string): never {
  throwExpression("Can't find " + elementId);
}

export interface MinusPlusButtonArgs {
  minusButtonId: string;
  minusButtonDisabledId: string;
  plusButtonId: string;
  plusButtonDisabledId: string;
  numId: string;
  min: number;
  max: number;
  /** If provided, what number the interface should start at. This value is clamped to be in [min, max].*/
  default?: number;
}

const clamp = (num: number, min: number, max: number) =>
  Math.min(Math.max(num, min), max);

class MinusPlusButton {
  args: MinusPlusButtonArgs;
  minusButton: HTMLElement;
  plusButton: HTMLElement;
  numField: HTMLElement;
  onNumFieldChangedCallback?: (num: number) => void;

  minusImageSwapper: ImageSwapper;
  plusImageSwapper: ImageSwapper;

  constructor(args: MinusPlusButtonArgs) {
    this.args = Object.assign({}, args);
    this.minusButton =
      document.getElementById(this.args.minusButtonDisabledId) ??
      throwMissingElement(this.args.minusButtonDisabledId);
    this.numField =
      document.getElementById(this.args.numId) ??
      throwMissingElement(this.args.numId);

    this.minusImageSwapper = new ImageSwapper(
      // The minus button starts in the disabled state.
      this.args.minusButtonDisabledId,
      this.args.minusButtonId
    );
    this.plusImageSwapper = new ImageSwapper(
      // The plus button starts in the enabled state.
      this.args.plusButtonId,
      this.args.plusButtonDisabledId
    );
    this.minusButton.addEventListener("click", () => {
      let num = this.getNum();
      if (num > this.args.min) {
        num -= 1;
        if (this.onNumFieldChangedCallback) {
          this.onNumFieldChangedCallback(num);
        }
        if (num == this.args.min) {
          this.minusImageSwapper.swap();
        }
        if (num == this.args.max - 1) {
          this.plusImageSwapper.swap();
        }
      }
      this.numField.innerHTML = num.toString();
    });

    this.plusButton =
      document.getElementById(this.args.plusButtonId) ??
      throwMissingElement(this.args.plusButtonId);
    this.plusButton.addEventListener("click", () => {
      let num = this.getNum();
      if (num < this.args.max) {
        num += 1;
        if (this.onNumFieldChangedCallback) {
          this.onNumFieldChangedCallback(num);
        }
        if (num == this.args.min + 1) {
          this.minusImageSwapper.swap();
        }
        if (num == this.args.max) {
          this.plusImageSwapper.swap();
        }
      }
      this.numField.innerHTML = num.toString();
    });
    if (this.args.default) {
      this.setNum(clamp(this.args.default, this.args.min, this.args.max));
    }
  }

  /** Updates the maximum to @param newMax and updates button states accordingly. */
  updateMax(newMax: number) {
    const oldMax = this.args.max;
    const oldNum = this.getNum();
    const plusButtonEnabled = oldMax != oldNum;
    const minusButtonEnabled = this.args.min != oldNum;

    this.args.max = newMax;

    if (oldNum >= this.args.max) {
      this.numField.innerHTML = newMax.toString();
      if (plusButtonEnabled) {
        this.plusImageSwapper.swap();
      }
      if (minusButtonEnabled && newMax == 1) {
        this.minusImageSwapper.swap();
      }
    } else {
      if (!plusButtonEnabled) {
        this.plusImageSwapper.swap();
      }
    }
  }
  /** Set the field to @param newNum updates button states accordingly. */
  setNum(newNum: number) {
    if (newNum < this.args.min) {
      throwExpression(
        `Can't set number to ${newNum} as it is less than the minimum: ${this.args.min}.`
      );
    }

    if (newNum > this.args.max) {
      throwExpression(
        `Can't set number to ${newNum} as it is more than the maximum: ${this.args.max}.`
      );
    }

    const oldNum = this.getNum();
    const plusButtonEnabled = this.args.max != oldNum;
    const minusButtonEnabled = this.args.min != oldNum;

    this.numField.innerHTML = newNum.toString();
    if (this.onNumFieldChangedCallback) {
      this.onNumFieldChangedCallback(newNum);
    }
    if (plusButtonEnabled) {
      if (newNum == this.args.max) {
        this.plusImageSwapper.swap();
      }
    } else {
      if (newNum != this.args.max) {
        this.plusImageSwapper.swap();
      }
    }
    if (minusButtonEnabled) {
      if (newNum == this.args.min) {
        this.minusImageSwapper.swap();
      }
    } else {
      if (newNum != this.args.min) {
        this.minusImageSwapper.swap();
      }
    }
  }

  getNum(): number {
    return parseInt(this.numField.innerHTML);
  }

  setOnNumFieldChangedCallback(callback: (num: number) => void) {
    this.onNumFieldChangedCallback = callback;
  }
}

export {
  ImageSwapper,
  setStyleToFlex,
  setStyleToInlineBlock,
  setStyleToNone,
  setStyleToBlock,
  setMessage,
  setOpacity,
  addHideOnEscapeKey,
  setDisabled,
  MinusPlusButton,
};
